<template>
  <header class="app-header">
    <div class="header-container">
      <router-link to="/" class="logo">
        <img src="@/assets/oqu1024blue.png" alt="Oqu logo" />
      </router-link>
      <nav class="main-nav">
        <router-link to="/">
          <i class="pi pi-home"></i>
          {{ $t('navigation.home') }}
        </router-link>
        <router-link to="/books">
          <i class="pi pi-book"></i>
          {{ $t('navigation.books') }}
        </router-link>
        <router-link v-if="authStore.isAdmin" to="/admin">
          <i class="pi pi-cog"></i>
          {{ $t('navigation.admin') }}
        </router-link>
        <router-link v-if="authStore.isAuthor" to="/author">
          <i class="pi pi-pencil"></i>
          {{ $t('navigation.author') }}
        </router-link>
        <router-link v-if="authStore.isEditor" to="/editor">
          <i class="pi pi-eye"></i>
          {{ $t('navigation.editor') }}
        </router-link>
        <router-link to="/subscription">
          <i class="pi pi-star"></i>
          {{ $t('subscription.title') }}
        </router-link>
      </nav>
      <div class="header-actions">
        <div v-if="authStore.isAuthenticated" class="user-menu">
          <button ref="userMenuTrigger" @click="toggleUserMenu" class="user-menu-btn">
            <i class="pi pi-user"></i>
            <span>{{ authStore.user?.username || authStore.user?.email }}</span>
            <i class="pi pi-chevron-down"></i>
          </button>
          
          <div v-if="userMenuOpen" class="user-dropdown">
            <router-link to="/profile" class="dropdown-item">
              <i class="pi pi-user"></i>
              Profile
            </router-link>
            <router-link v-if="authStore.isAdmin" to="/admin" class="dropdown-item">
              <i class="pi pi-cog"></i>
              Admin Dashboard
            </router-link>
            <button @click="logout" class="dropdown-item">
              <i class="pi pi-sign-out"></i>
              Logout
            </button>
          </div>
        </div>
        <div v-else class="auth-links">
          <router-link to="/login" class="btn btn-secondary">{{ $t('navigation.login') }}</router-link>
          <router-link to="/register" class="btn btn-primary">{{ $t('navigation.register') }}</router-link>
        </div>
        <language-switcher />
      </div>
    </div>
  </header>
</template>

<script>
import { defineComponent, ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'

export default defineComponent({
  name: 'AppHeader',
  components: {
    LanguageSwitcher
  },
  setup() {
    const authStore = useAuthStore();
    const router = useRouter();
    const userMenuOpen = ref(false);
    const userMenuTrigger = ref(null);
    
    const toggleUserMenu = () => {
      userMenuOpen.value = !userMenuOpen.value;
    };
    
    const logout = () => {
      authStore.logout();
      router.push('/login');
    };
    
    // Close menu when clicking outside
    const handleClickOutside = (event) => {
      if (userMenuTrigger.value && !userMenuTrigger.value.contains(event.target)) {
        userMenuOpen.value = false;
      }
    };
    
    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
    });
    
    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
    });
    
    return {
      authStore,
      userMenuOpen,
      userMenuTrigger,
      toggleUserMenu,
      logout
    };
  }
});
</script>

<style lang="scss" scoped>
.app-header {
  background: var(--header-bg);
  padding: 1rem 2rem;
  box-shadow: 0 4px 20px rgba(0, 153, 255, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid var(--border-color);
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.logo {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: var(--text-color);
  font-size: 1.5rem;
  font-weight: 700;

  img {
    height: 40px;
    margin-right: 0.5rem;
  }
}

.main-nav {
  display: flex;
  gap: 2rem;

  a {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    text-decoration: none;
    color: var(--text-light);
    font-weight: 600;
    padding: 0.5rem;
    position: relative;
    transition: color 0.3s ease;

    i {
      font-size: 1.125rem;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 0;
      height: 2px;
      background-color: var(--primary-color);
      transition: width 0.3s ease;
    }

    &:hover,
    &.router-link-exact-active {
      color: var(--primary-color);
    }
    
    &.router-link-exact-active::after {
      width: 100%;
    }
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.user-menu {
  position: relative;
}

.user-menu-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: var(--secondary-color);
  border: 1px solid var(--border-color);
  border-radius: 30px;
  cursor: pointer;
  color: var(--text-color);
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
  }
}

.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px rgba(0, 153, 255, 0.15);
  min-width: 200px;
  z-index: 1000;
  border: 1px solid var(--border-color);
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  color: var(--text-color);
  text-decoration: none;
  width: 100%;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font-size: 0.875rem;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--secondary-color);
    color: var(--primary-color);
  }

  &:first-child {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }

  &:last-child {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }
}

.auth-links {
  display: flex;
  gap: 1rem;
}
</style>